import React from 'react'
import {useOnUnmount} from './onUnmount'

/**
 * Creates React.useState to handle immediate change to UI.
 * Changes to this state are debounced and "debouncedFunc" is called to store the value (most likely in Mobx state).
 * Automatically flushes the state when hook is unmounted (can be disabled with "preventFlushOnUnmount").
 *
 * Warning: this hook will NOT react to changes of "initialState". Use useStateReactiveWithDebounce if that is wanted behaviour.
 *
 * @param initialState Initial value of React state (same as React.useState).
 * @param debouncedFunc Function called after React state didn't change for "delay" milliseconds.
 * @param delay Delay after "debouncedFunc" is called when React state stops changing.
 * @param preventFlushOnUnmount Prevents final flush when the hook is unmounted.
 * @returns React state value and state setter (same as React.useState). Third returned value is function to flush debounced state.
 */
export function useStateWithDebounce<T>(
    initialState: T | (() => T),
    debouncedFunc: (value: T) => void,
    delay: number,
    preventFlushOnUnmount = false
): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
    const changed = React.useRef(false)
    const timerRef = React.useRef<ReturnType<typeof setTimeout>>()

    const [state, setState] = React.useState(initialState)

    const setStateAndDebounce = React.useCallback((value: React.SetStateAction<T>) => {
        setState(value)
        changed.current = true
    }, [])

    // Core debounce effect
    React.useEffect(() => {
        if (changed.current) {
            timerRef.current = setTimeout(() => {
                debouncedFunc(state)
                timerRef.current = undefined
            }, delay)
        }
    }, [state, debouncedFunc, delay])

    const flush = React.useCallback(() => {
        if (timerRef.current) {
            debouncedFunc(state)
            clearTimeout(timerRef.current)
            timerRef.current = undefined
        }
    }, [debouncedFunc, state])

    useOnUnmount(() => {
        if (!preventFlushOnUnmount) {
            flush()
        }
    })

    return [state, setStateAndDebounce, flush]
}
